'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { fetchHeader } from '@/shared/store/slices/headerSlice';
import type { RootState, AppDispatch } from '@/shared/store/store';
import { useApiState } from '@/shared/hooks/useApiState';
import { Logo } from '@/shared/ui/molecules';
import { AppLink, Button } from '@/shared/ui/atoms';
import type { MouseEvent } from 'react';
import { HiOutlineMenu } from 'react-icons/hi';
import gsap from 'gsap';
import styles from './header.module.scss';

const getSectionId = (href: string) => (href.startsWith('#') ? href.slice(1) : null);

const Header = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { logo, nav } = useSelector((state: RootState) => state.header);
    const { loading, error } = useApiState('header');
    const [activeSection, setActiveSection] = useState('home');

    const navRef = useRef<HTMLElement>(null);
    const indicatorRef = useRef<HTMLDivElement>(null);
    const isFirstRun = useRef(true);

    const scrollToSection = useCallback((id: string) => {
        setActiveSection(id);
        const element = document.getElementById(id);
        element?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
        dispatch(fetchHeader());
    }, [dispatch]);

    // Актуальне значення для runUpdate (у rAF closure може бути stale)
    const activeSectionRef = useRef(activeSection);
    activeSectionRef.current = activeSection;

    // Індикатор: оновлення при завантаженні і при зміні activeSection/nav
    useEffect(() => {
        const runUpdate = () => {
            const navEl = navRef.current;
            const indicator = indicatorRef.current;
            if (!navEl || !indicator || nav.length === 0) return;

            const currentSection = activeSectionRef.current;
            const activeIndex = nav.findIndex((item) => getSectionId(item.href) === currentSection);
            if (activeIndex === -1) return;

            const links = navEl.querySelectorAll<HTMLAnchorElement>('a[href^="#"]');
            const activeItem = links[activeIndex];
            if (!activeItem) return;

            const navRect = navEl.getBoundingClientRect();
            const itemRect = activeItem.getBoundingClientRect();

            const left = itemRect.left - navRect.left;
            const width = itemRect.width;
            const height = itemRect.height;
            const top = itemRect.top - navRect.top;

            if (width <= 0 || height <= 0) return;

            const positionVars = { left, top, width, height };

            if (isFirstRun.current) {
                gsap.set(indicator, positionVars);
                isFirstRun.current = false;
                return;
            }

            gsap.killTweensOf(indicator);
            gsap.fromTo(
                indicator,
                { scaleX: 1.08, scaleY: 0.95 },
                {
                    ...positionVars,
                    scaleX: 1,
                    scaleY: 1,
                    duration: 0.4,
                    ease: 'power2.out',
                    overwrite: true,
                }
            );
        };

        requestAnimationFrame(() => {
            requestAnimationFrame(runUpdate);
        });
    }, [activeSection, nav, loading]);

    if (loading) return <header className={styles.header}>Loading header...</header>;
    if (error) return <header className={styles.header}>Error: {error}</header>;

    return (
        <header className={styles.header}>
            <div className={styles.headerGlow} aria-hidden />
            <div className={styles.headerInner}>
                <div className={styles.logo}>
                    <Logo
                        src={logo.src}
                        alt={logo.alt}
                        width={logo.width}
                        height={logo.height}
                        text={logo.text}
                        href="#home"
                        className={styles.link}
                        onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                            e.preventDefault();
                            scrollToSection('home');
                        }}
                    />
                </div>

                <nav ref={navRef} className={styles.nav}>
                    <div ref={indicatorRef} className={styles.navIndicator} aria-hidden />
                    {nav.map((item) => {
                        const sectionId = getSectionId(item.href);
                        const isActive = activeSection === sectionId;
                        return (
                            <AppLink
                                key={item.href}
                                href={item.href}
                                className={clsx(styles.navLink, isActive && styles.navLinkActive)}
                                onClick={(e) => {
                                    if (sectionId) {
                                        e.preventDefault();
                                        scrollToSection(sectionId);
                                    }
                                }}
                            >
                                {item.name}
                            </AppLink>
                        );
                    })}
                </nav>

                <Button
                    type="button"
                    className={styles.menuButton}
                    aria-label="Open menu"
                    startIcon={<HiOutlineMenu className={styles.menuButtonIcon} />}
                />
            </div>
        </header>
    );
}

export default Header;
